import { Link } from "react-router-dom";
import SEO, { getServiceSchema, getBreadcrumbSchema } from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Camera, Globe, Target, Users, CheckCircle2, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";

const MarketingProcess = () => {
  const marketingSteps = [
    {
      icon: Camera,
      title: "Professional Photography & Staging",
      description: "High-quality photos that showcase your property's best features. We know what Kansas City renters look for in Overland Park, Leawood, and surrounding areas.",
      timeline: "Day 1-2",
      details: [
        "Professional photographer",
        "Staging recommendations",
        "Virtual tours (when needed)",
        "Drone footage for premium properties"
      ]
    },
    {
      icon: Target,
      title: "Strategic Pricing Analysis",
      description: "Data-driven pricing based on Johnson County market conditions, comparable rentals, and seasonal demand to maximize rent while minimizing vacancy.",
      timeline: "Day 1",
      details: [
        "Comparative market analysis",
        "Neighborhood-specific pricing",
        "Seasonal adjustments",
        "Optimal rent positioning"
      ]
    },
    {
      icon: Globe,
      title: "Multi-Platform Syndication",
      description: "Your property appears on 50+ rental sites including Zillow, Apartments.com, Trulia, and local Kansas listings - reaching thousands of qualified renters.",
      timeline: "Day 3-5",
      details: [
        "MLS listing",
        "Zillow, Trulia, Apartments.com",
        "Facebook Marketplace",
        "Local Kansas City rental sites"
      ]
    },
    {
      icon: Users,
      title: "Showing & Lead Management",
      description: "We handle all inquiries, schedule showings, and pre-qualify prospects. Professional showings 7 days a week with instant follow-up.",
      timeline: "Ongoing",
      details: [
        "24/7 inquiry response",
        "Scheduled & self-showing options",
        "Lead pre-qualification",
        "Application processing"
      ]
    }
  ];

  const results = [
    { metric: "Fast", label: "Vacancy Turnaround" },
    { metric: "50+", label: "Listing Platforms" },
    { metric: "95%", label: "Occupancy Rate" },
    { metric: "Strong", label: "Inquiry Volume" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Property Marketing Process | Rently Properties Kansas City"
        description="Learn how we market rental properties in Overland Park, Leawood, Lenexa & Olathe. Professional photos, 50+ listing platforms, fast vacancy turnaround in Johnson County KS."
        canonical="/marketing-process"
        keywords="rental property marketing Kansas City, Overland Park rental listing, Johnson County property advertising, Kansas City tenant placement, vacancy marketing"
        schema={[
          getServiceSchema(
            "Rental Property Marketing",
            "Multi-platform rental marketing with professional photography, strategic pricing, and 50+ listing syndication for properties in Johnson County, Kansas."
          ),
          getBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Marketing Process", path: "/marketing-process" }
          ])
        ]}
      />
      <Header />

      <main>
        {/* Hero Section */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Badge className="mb-6 bg-secondary">Proven Marketing System</Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
              How We Market Your <span className="text-primary">Kansas Rental Property</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Our aggressive marketing strategy fills vacancies fast with quality tenants across Overland Park, Leawood, Lenexa, and Olathe.
            </p>
            <Button size="lg" className="bg-secondary hover:bg-secondary/90" asChild>
              <Link to="/#contact">Get Started Today</Link>
            </Button>
          </div>
        </section>

        {/* Marketing Steps */}
        <section className="py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {marketingSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15, duration: 0.5 }}
                  >
                    <Card className="h-full">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-4">
                          <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                            <Icon className="h-6 w-6" />
                          </div>
                          <Badge variant="outline">{step.timeline}</Badge>
                        </div>
                        <CardTitle className="font-display text-xl mb-2">
                          {step.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                          {step.description}
                        </p>
                        <ul className="space-y-2">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Proven Results in Johnson County
              </h2>
              <p className="text-lg text-muted-foreground">
                Our marketing system delivers measurable results for Kansas property owners
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {results.map((result, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="text-center"
                >
                  <div className="text-4xl md:text-5xl font-bold font-display text-primary mb-2">
                    {result.metric}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {result.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <TrendingUp className="h-12 w-12 text-primary mx-auto mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Fill Your Vacancy Fast
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Let our proven marketing system find quality tenants for your Kansas City metro property.
            </p>
            <Button size="lg" className="bg-secondary hover:bg-secondary/90" asChild>
              <Link to="/#contact">Request Marketing Plan</Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MarketingProcess;
